/**
 * Silo Anywhere — the full-width design panel (P3).
 *
 * Opens from the rail card in Silo Settings. Left column: every look, limit
 * and domain control. Right column: a live preview that redraws as you type.
 *
 * The preview draws SAMPLE text. It never calls the Q&A engine — answering
 * lives on /api/s/{slug}/ask and nowhere else, and a preview that quietly
 * spent the owner's fuel every keystroke would be a trap.
 *
 * The embed snippet is deliberately absent here: design is stored server-side
 * and delivered by token, so changing colours never changes the line of code
 * already pasted on the customer's site. That promise is the whole point of
 * configuring server-side, so the panel says it out loud.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sparkle, ArrowCounterClockwise, FloppyDisk, Warning, Plus, X,
  ChatCircleDots, ArrowSquareOut, DotsThree,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Fractions of a cent per question, so whole dollars would read "$0" forever.
const fuel = (cents) => {
  const c = Number(cents || 0);
  if (!c) return "$0.00";
  return c < 100 ? `$${(c / 100).toFixed(3)}` : `$${(c / 100).toFixed(2)}`;
};


const ICONS = [
  { value: "sparkle", label: "Sparkle" },
  { value: "chat", label: "Chat bubble" },
  { value: "question", label: "Question mark" },
  { value: "avatar", label: "Avatar only" },
];

/* ---- WCAG contrast, so a pretty palette can't ship unreadable ---- */
const hexToRgb = (hex) => {
  const h = String(hex || "").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  if (full.length !== 6) return null;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const luminance = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
export const contrastRatio = (a, b) => {
  const la = luminance(a);
  const lb = luminance(b);
  if (la === null || lb === null) return null;
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
};

const Section = ({ title, hint, children }) => (
  <div className="border-t hairline pt-4 mt-4 first:border-0 first:pt-0 first:mt-0">
    <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68]">{title}</div>
    {hint && <p className="text-xs text-[#6B6A68] mt-1">{hint}</p>}
    <div className="mt-3 space-y-3">{children}</div>
  </div>
);

const ColorField = ({ label, value, onChange, name }) => (
  <label className="flex items-center justify-between gap-3">
    <span className="text-sm text-[#1C1B1A]">{label}</span>
    <span className="flex items-center gap-2">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`sa-hex-${name}`}
        aria-label={`${label} hex value`}
        className="w-[84px] text-xs font-mono px-2 py-1.5 border hairline rounded bg-white focus:outline-none focus:border-[#4A5D4E]"
      />
      <input
        type="color"
        value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000"}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`sa-color-${name}`}
        aria-label={label}
        className="w-8 h-8 rounded border hairline bg-white cursor-pointer"
      />
    </span>
  </label>
);

const TextField = ({ label, value, onChange, name, placeholder, maxLength }) => (
  <label className="block">
    <span className="text-sm text-[#6B6A68]">{label}</span>
    <input
      type="text"
      value={value || ""}
      maxLength={maxLength}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`sa-field-${name}`}
      className="mt-1 w-full text-sm px-3 py-2 border hairline rounded-md bg-white focus:outline-none focus:border-[#4A5D4E]"
    />
  </label>
);

const NumField = ({ label, value, onChange, name, min, max, suffix }) => (
  <label className="flex items-center justify-between gap-3">
    <span className="text-sm text-[#1C1B1A]">{label}</span>
    <span className="flex items-center gap-2">
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`sa-field-${name}`}
        className="w-[84px] text-sm px-2 py-1.5 border hairline rounded bg-white focus:outline-none focus:border-[#4A5D4E]"
      />
      {suffix && <span className="text-xs text-[#6B6A68] w-6">{suffix}</span>}
    </span>
  </label>
);

const Choice = ({ label, value, onChange, options, name }) => (
  <div>
    <div className="text-sm text-[#6B6A68] mb-1.5">{label}</div>
    <div className="inline-flex rounded-md border hairline overflow-hidden" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          data-testid={`sa-${name}-${o.value}`}
          className={`px-3 py-1.5 text-xs transition-colors ${
            value === o.value ? "bg-[#1C1B1A] text-[#FDFCF8]" : "bg-white text-[#6B6A68] hover:text-[#1C1B1A]"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

/* ------------------------------- preview ------------------------------- */
function WidgetPreview({ cfg, mode }) {
  const t = cfg.theme || {};
  const dark = t.theme === "dark";
  const canvas = t.canvas_color;
  const radius = `${t.radius_px ?? 14}px`;
  const bubble = (
    <div
      className="flex items-center justify-center shrink-0"
      style={{ width: 56, height: 56, borderRadius: 999, background: t.bubble_color,
               boxShadow: "0 8px 24px -8px rgba(0,0,0,0.45)" }}
      aria-hidden="true"
    >
      {cfg.avatar_url
        ? <img src={cfg.avatar_url} alt="" style={{ width: 34, height: 34, borderRadius: 999, objectFit: "cover" }} />
        : <Sparkle size={24} weight="duotone" color={dark ? "#1C1B1A" : "#FDFCF8"} />}
    </div>
  );

  return (
    <div data-testid="sa-preview">
      <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68]">Preview</div>
      <p className="text-xs text-[#6B6A68] mt-1 mb-3">
        Sample text only. Nothing is asked of your silo from here, so this costs you nothing.
      </p>

      {mode === "launcher" ? (
        <div className="rounded-lg border hairline bg-[#F0F1EE] p-6 flex flex-col items-center gap-3">
          {bubble}
          <div className="text-xs text-[#6B6A68] text-center max-w-[220px]">
            Link Launcher. A tap opens your full Public Q&amp;A page in a new tab, with your
            referral link attached.
          </div>
        </div>
      ) : (
        <div className="rounded-lg border hairline bg-[#F0F1EE] p-4 flex flex-col items-end gap-3">
          <div
            style={{
              width: Math.min(t.desktop_w || 380, 360),
              height: Math.min(t.desktop_h || 560, 420),
              background: canvas,
              border: `1px solid ${t.border_color}`,
              borderRadius: radius,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 18px 48px -18px rgba(0,0,0,0.45)",
            }}
          >
            <div
              style={{ background: t.header_color, padding: "10px 12px", display: "flex",
                       alignItems: "center", gap: 8 }}
            >
              {cfg.avatar_url
                ? <img src={cfg.avatar_url} alt="" style={{ width: 22, height: 22, borderRadius: 999, objectFit: "cover" }} />
                : <Sparkle size={16} weight="duotone" color={dark ? "#1C1B1A" : "#FDFCF8"} />}
              <span style={{ color: dark ? "#1C1B1A" : "#FDFCF8", fontSize: 13, fontWeight: 500, flex: 1 }}>
                {cfg.assistant_name || "Assistant"}
              </span>
              <DotsThree size={18} color={dark ? "#1C1B1A" : "#FDFCF8"} />
            </div>

            <div style={{ flex: 1, padding: 12, display: "flex", flexDirection: "column", gap: 8, overflow: "hidden" }}>
              <div style={{ background: t.assistant_msg_color, color: t.text_primary,
                            padding: "8px 10px", borderRadius: radius, fontSize: 12.5, maxWidth: "88%" }}>
                {cfg.greeting || "Ask me anything."}
              </div>
              <div style={{ background: t.user_msg_color, color: "#FDFCF8", padding: "8px 10px",
                            borderRadius: radius, fontSize: 12.5, maxWidth: "80%", alignSelf: "flex-end" }}>
                Sample visitor question.
              </div>
              <div style={{ background: t.assistant_msg_color, color: t.text_primary,
                            padding: "8px 10px", borderRadius: radius, fontSize: 12.5, maxWidth: "88%" }}>
                Sample answer in your voice, grounded in your silo.
              </div>
              <div style={{ color: t.text_secondary, fontSize: 10.5, marginTop: "auto" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <ArrowSquareOut size={10} /> Open full Silo experience
                </span>
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${t.border_color}`, padding: 10, display: "flex", gap: 8 }}>
              <div style={{ flex: 1, border: `1px solid ${t.border_color}`, borderRadius: radius,
                            padding: "7px 10px", color: t.text_secondary, fontSize: 12 }}>
                Type a question…
              </div>
              <div style={{ background: t.accent_color, color: "#FDFCF8", borderRadius: radius,
                            padding: "7px 11px", fontSize: 12 }}>Ask</div>
            </div>
          </div>
          <div style={{ alignSelf: t.position === "bl" ? "flex-start" : "flex-end" }}>{bubble}</div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------- panel -------------------------------- */
export default function SiloAnywherePanel({ open, onOpenChange, siloId, config, onSaved }) {
  const [draft, setDraft] = useState(config);
  const [saving, setSaving] = useState(false);
  const [newOrigin, setNewOrigin] = useState("");

  useEffect(() => { if (open) setDraft(config); }, [open, config]);

  const t = draft?.theme || {};
  const setTheme = (k, v) => setDraft((d) => ({ ...d, theme: { ...d.theme, [k]: v } }));
  const setLimit = (k, v) => setDraft((d) => ({ ...d, limits: { ...d.limits, [k]: v } }));
  const setTop = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  // Two pairs carry the actual reading load: body text on the assistant bubble,
  // and the secondary type on the canvas. Anything under 4.5:1 is flagged by
  // name so it's obvious which swatch to move.
  const warnings = useMemo(() => {
    if (!draft) return [];
    const pairs = [
      ["Message text on assistant bubbles", t.text_primary, t.assistant_msg_color],
      ["Secondary text on the chat background", t.text_secondary, t.canvas_color],
    ];
    return pairs
      .map(([label, fg, bg]) => ({ label, ratio: contrastRatio(fg, bg) }))
      .filter((x) => x.ratio !== null && x.ratio < 4.5);
  }, [draft, t]);

  const restoreLook = () => {
    const def = config?.defaults?.theme;
    if (!def) return;
    setDraft((d) => ({ ...d, theme: { ...def } }));
    toast.success("Default look restored. Save to apply it.");
  };

  const addOrigin = () => {
    const v = newOrigin.trim();
    if (!v) return;
    setDraft((d) => ({ ...d, allowed_origins: [...(d.allowed_origins || []), v] }));
    setNewOrigin("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/silos/${siloId}/widget`, {
        mode: draft.mode,
        greeting: draft.greeting,
        assistant_name: draft.assistant_name,
        avatar_url: draft.avatar_url || null,
        owner_logo_url: draft.owner_logo_url || null,
        origin_mode: draft.origin_mode,
        allowed_origins: draft.allowed_origins || [],
        theme: draft.theme,
        limits: draft.limits,
      });
      onSaved?.(r.data);
      setDraft(r.data);
      toast.success("Silo Anywhere look saved. Your embed code is unchanged.");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the widget settings.");
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl bg-[#FDFCF8] p-6 sm:p-8 overflow-y-auto max-h-[92vh]"
        data-testid="silo-anywhere-panel"
      >
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5D4E] mb-1 flex items-center gap-2">
            <ChatCircleDots size={12} weight="duotone" /> Silo Anywhere
          </div>
          <DialogTitle className="font-serif-display text-2xl sm:text-3xl text-[#1C1B1A]">
            Design your widget.
          </DialogTitle>
          <DialogDescription className="text-sm text-[#6B6A68]">
            Everything here is stored on your silo and delivered to the widget by its token, so the
            line of code on your website never has to change.
          </DialogDescription>
        </DialogHeader>

        <div className="grid lg:grid-cols-[1fr_380px] gap-8 mt-4">
          <div>
            <Section title="Mode">
              <Choice
                label="How the widget behaves"
                name="mode"
                value={draft.mode}
                onChange={(v) => setTop("mode", v)}
                options={[
                  { value: "chat", label: "Chat on my website" },
                  { value: "launcher", label: "Open My Silo in a new tab" },
                ]}
              />
              <p className="text-xs text-[#6B6A68]">
                {draft.mode === "chat"
                  ? "Visitors ask and read answers on your page, with an \u201cOpen full Silo experience\u201d link to your Public Q&A."
                  : "The bubble opens your Public Q&A page in a new tab. Nothing is embedded, so it works even where a site blocks framing."}
              </p>
            </Section>

            <Section title="Identity">
              <TextField label="Assistant name" name="assistant_name" maxLength={40}
                value={draft.assistant_name} onChange={(v) => setTop("assistant_name", v)} />
              <TextField label="Greeting" name="greeting" maxLength={200}
                value={draft.greeting} onChange={(v) => setTop("greeting", v)} />
              <TextField label="Avatar image URL" name="avatar_url" placeholder="https://…"
                value={draft.avatar_url} onChange={(v) => setTop("avatar_url", v)} />
              <TextField label="Your logo URL (optional)" name="owner_logo_url" placeholder="https://…"
                value={draft.owner_logo_url} onChange={(v) => setTop("owner_logo_url", v)} />
              <p className="text-xs text-[#6B6A68]">Image URLs must start with https.</p>
            </Section>

            <Section title="Launcher">
              <Choice label="Icon" name="icon" value={t.launcher_icon}
                onChange={(v) => setTheme("launcher_icon", v)} options={ICONS} />
              <Choice label="Corner" name="position" value={t.position}
                onChange={(v) => setTheme("position", v)}
                options={[{ value: "br", label: "Bottom right" }, { value: "bl", label: "Bottom left" }]} />
              <ColorField label="Bubble" name="bubble_color" value={t.bubble_color}
                onChange={(v) => setTheme("bubble_color", v)} />
            </Section>

            <Section title="Panel">
              <Choice label="Theme" name="theme" value={t.theme}
                onChange={(v) => setTheme("theme", v)}
                options={[{ value: "light", label: "Light" }, { value: "dark", label: "Dark" }]} />
              <ColorField label="Chat background" name="canvas_color" value={t.canvas_color}
                onChange={(v) => setTheme("canvas_color", v)} />
              <ColorField label="Header" name="header_color" value={t.header_color}
                onChange={(v) => setTheme("header_color", v)} />
              <ColorField label="Border" name="border_color" value={t.border_color}
                onChange={(v) => setTheme("border_color", v)} />
              <NumField label="Corner radius" name="radius_px" value={t.radius_px} min={0} max={32}
                suffix="px" onChange={(v) => setTheme("radius_px", v)} />
              <NumField label="Desktop width" name="desktop_w" value={t.desktop_w} min={300} max={480}
                suffix="px" onChange={(v) => setTheme("desktop_w", v)} />
              <NumField label="Desktop height" name="desktop_h" value={t.desktop_h} min={400} max={720}
                suffix="px" onChange={(v) => setTheme("desktop_h", v)} />
              <Choice label="On a phone" name="mobile" value={t.mobile}
                onChange={(v) => setTheme("mobile", v)}
                options={[{ value: "sheet", label: "Bottom sheet" }, { value: "full", label: "Full screen" }]} />
            </Section>

            <Section title="Messages and type">
              <ColorField label="Visitor message" name="user_msg_color" value={t.user_msg_color}
                onChange={(v) => setTheme("user_msg_color", v)} />
              <ColorField label="Assistant message" name="assistant_msg_color" value={t.assistant_msg_color}
                onChange={(v) => setTheme("assistant_msg_color", v)} />
              <ColorField label="Primary text" name="text_primary" value={t.text_primary}
                onChange={(v) => setTheme("text_primary", v)} />
              <ColorField label="Secondary text" name="text_secondary" value={t.text_secondary}
                onChange={(v) => setTheme("text_secondary", v)} />
              <ColorField label="Button / accent" name="accent_color" value={t.accent_color}
                onChange={(v) => setTheme("accent_color", v)} />
              {warnings.length > 0 && (
                <div className="rounded-md border border-[#C16E5A]/35 bg-[#C16E5A]/[0.07] p-3"
                     data-testid="sa-contrast-warning">
                  <div className="text-xs font-medium text-[#8A3A1C] flex items-center gap-1.5">
                    <Warning size={12} weight="duotone" /> Hard to read
                  </div>
                  <ul className="text-[11.5px] text-[#8A3A1C] mt-1 space-y-0.5">
                    {warnings.map((w) => (
                      <li key={w.label}>{w.label} sits at {w.ratio.toFixed(1)}:1. Readable text needs 4.5:1.</li>
                    ))}
                  </ul>
                </div>
              )}
            </Section>

            <Section title="Allowed websites"
                     hint="Exact domains. www and non-www count as the same entry, and a subdomain is its own entry.">
              <Choice label="Where the widget may load" name="origin-mode" value={draft.origin_mode}
                onChange={(v) => setTop("origin_mode", v)}
                options={[{ value: "any", label: "Any website" }, { value: "list", label: "Only these" }]} />
              {draft.origin_mode === "list" && (
                <div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newOrigin}
                      placeholder="yoursite.com"
                      onChange={(e) => setNewOrigin(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOrigin(); } }}
                      data-testid="sa-origin-input"
                      className="flex-1 text-sm px-3 py-2 border hairline rounded-md bg-white focus:outline-none focus:border-[#4A5D4E]"
                    />
                    <button type="button" onClick={addOrigin} data-testid="sa-origin-add"
                      className="px-3 py-2 rounded-md border hairline text-xs hover:border-[#4A5D4E] flex items-center gap-1">
                      <Plus size={12} /> Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(draft.allowed_origins || []).map((h) => (
                      <span key={h} className="text-xs bg-[#F0F1EE] border hairline rounded px-2 py-1 flex items-center gap-1.5">
                        {h}
                        <button type="button" aria-label={`Remove ${h}`} data-testid={`sa-origin-remove-${h}`}
                          onClick={() => setDraft((d) => ({ ...d, allowed_origins: d.allowed_origins.filter((x) => x !== h) }))}
                          className="text-[#6B6A68] hover:text-[#8A3A1C]">
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                    {!(draft.allowed_origins || []).length && (
                      <span className="text-xs text-[#6B6A68]">No domains yet, so nothing can load the widget.</span>
                    )}
                  </div>
                </div>
              )}
              {/* P8 — the refused-website diagnostic. Before this, a wrong
                  allow-list looked identical to a broken product: the 403
                  happened on the customer's site and vanished. */}
              {!!(config?.origin_blocks || []).length && (
                <div className="mt-3 rounded-md border hairline bg-[#FDF6F2] p-2.5" data-testid="sa-origin-blocks">
                  <div className="text-xs text-[#8A3A1C]">Refused websites</div>
                  <p className="text-[10.5px] text-[#6B6A68] mt-1 mb-2 leading-relaxed">
                    Your snippet was loaded from a domain that is not on the list above, so the
                    widget refused to answer there. If one of these is yours, allow it — it is
                    usually a www, a subdomain or a staging URL. Last 20 kept.
                  </p>
                  <ul className="space-y-1">
                    {[...(config.origin_blocks || [])].reverse().map((b) => {
                      const allowed = (draft.allowed_origins || []).includes(b.origin);
                      const allowable = /^[a-z0-9.-]+\.[a-z]{2,}$/.test(b.origin || "");
                      return (
                        <li key={b.origin} className="flex items-center justify-between gap-2 text-xs">
                          <span className="truncate">
                            <span className="text-[#1C1B1A]">{b.origin}</span>
                            <span className="text-[#6B6A68]">
                              {` · ${b.count || 1} ${(b.count || 1) === 1 ? "try" : "tries"}`}
                              {b.at ? ` · ${new Date(b.at).toLocaleString()}` : ""}
                            </span>
                          </span>
                          {draft.origin_mode === "list" && allowable && (allowed
                            ? <span className="shrink-0 text-[10px] text-[#4A5D4E]">on the list</span>
                            : (
                              <button type="button" data-testid={`sa-origin-allow-${b.origin}`}
                                onClick={() => setDraft((d) => ({
                                  ...d, allowed_origins: [...(d.allowed_origins || []), b.origin],
                                }))}
                                className="shrink-0 text-[10px] px-2 py-0.5 rounded border hairline bg-white hover:border-[#4A5D4E]">
                                Allow
                              </button>
                            ))}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </Section>

            <Section title="Usage limits"
                     hint="Widget questions run on your fuel, not the visitor's dots, and never require your Public Q&A subscription. These caps are what stop a busy website draining it.">
              <NumField label="Longest question" name="max_msg_chars" value={draft.limits?.max_msg_chars}
                min={100} max={4000} suffix="chars" onChange={(v) => setLimit("max_msg_chars", v)} />
              <NumField label="Per visitor, per day" name="per_visitor_daily" value={draft.limits?.per_visitor_daily}
                min={1} max={500} onChange={(v) => setLimit("per_visitor_daily", v)} />
              <NumField label="Across the widget, per day" name="per_widget_daily" value={draft.limits?.per_widget_daily}
                min={10} max={20000} onChange={(v) => setLimit("per_widget_daily", v)} />
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="mt-1" data-testid="sa-field-alert_email"
                  checked={!!draft.limits?.alert_email}
                  onChange={(e) => setLimit("alert_email", e.target.checked)} />
                <span className="text-sm text-[#1C1B1A]">
                  Email me when a cap is hit
                  <span className="block text-xs text-[#6B6A68]">
                    One email per day, naming the limit that filled. So a quiet ceiling never
                    looks like a broken widget.
                  </span>
                </span>
              </label>
            </Section>

            {config?.usage && (
              <Section title="Widget usage"
                       hint="Counted separately from your full Public Q&A page. The across-the-widget cap is the ceiling nothing can go past.">
                <dl className="rounded-md bg-[#F0F1EE] border hairline divide-y hairline" data-testid="sa-usage-detail">
                  {[
                    ["Questions today", `${config.usage.asks_today ?? 0} of ${config.usage.daily_limit ?? 0}`, "today"],
                    ["Left today", String(config.usage.remaining ?? 0), "remaining"],
                    ["Per visitor, per day", String(config.usage.per_visitor_daily ?? 0), "per-visitor"],
                    ["Per address, per day", String(config.widget_ip_daily_cap ?? 0), "per-ip"],
                    ["Fuel used today", fuel(config.usage.cost_cents_today), "fuel-today"],
                    ["Fuel used all time", fuel(config.usage.cost_cents_total), "fuel-total"],
                    ["Blocked today", String(config.usage.blocked_today ?? 0), "blocked"],
                    ["Questions all time", String(config.usage.asks_total ?? 0), "all-time"],
                    ["Last question", config.usage.last_ask_at ? new Date(config.usage.last_ask_at).toLocaleString() : "—", "last"],
                    // P7 — the owner's own testing on their silo pages, counted
                    // against its own ceiling so it can never spend the daily
                    // questions bought for the website.
                    ["Your testing today", `${config.usage.preview_today ?? 0} of ${config.usage.preview_limit ?? 0}`, "preview-today"],
                    ["Fuel on your testing", fuel(config.usage.preview_cost_cents_today), "preview-fuel"],
                    // P8 — one email per widget per day, so this has to show
                    // both what was emailed and what tripped afterwards.
                    ["Limit email today", config.usage.alerted_today
                      ? (config.usage.alert_reasons || []).join(" · ")
                      : (config.usage.alert_email_on ? "none" : "off"), "alert"],
                  ].map(([k, v, tid]) => (
                    <div key={k} className="flex items-center justify-between px-3 py-1.5 gap-3">
                      <dt className="text-xs text-[#6B6A68]">{k}</dt>
                      <dd className="text-xs text-[#1C1B1A] tabular-nums text-right" data-testid={`sa-usage-${tid}`}>{v}</dd>
                    </div>
                  ))}
                </dl>
              </Section>
            )}
          </div>

          <div className="lg:sticky lg:top-0 lg:self-start">
            <WidgetPreview cfg={draft} mode={draft.mode} />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 mt-6 pt-4 border-t hairline">
          <button type="button" onClick={restoreLook} data-testid="sa-restore-defaults"
            className="text-xs px-3 py-2 rounded-md border hairline hover:border-[#4A5D4E] flex items-center gap-1.5">
            <ArrowCounterClockwise size={12} /> Restore default look
          </button>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => onOpenChange(false)} data-testid="sa-cancel"
              className="text-xs px-3 py-2 rounded-md border hairline hover:border-[#4A5D4E]">Cancel</button>
            <button type="button" onClick={save} disabled={saving} data-testid="sa-save"
              className="px-5 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm disabled:opacity-60 flex items-center gap-1.5">
              <FloppyDisk size={14} weight="duotone" /> {saving ? "Saving…" : "Save look"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
